import React from "react";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { camelize } from "humps";
import { searchCategories } from "main/javascripts/constants/SearchCategories";
import { Icon } from "main/javascripts/components/atoms/Icon";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import {
  changeSearchCategory,
  searchCategorySelector,
} from "main/javascripts/features/search/searchPageSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { accentColor } from "main/javascripts/styles/base/colorStyle";
import {
  fontSize,
  fontWeight,
} from "main/javascripts/styles/base/typographyStyle";
import { postSelector } from "../postSlice";

interface IProps {
  borderStyleKey?: keyof IBorderStyle;
}

export const InPostSearchCategoryTab: React.FC<IProps> = (
  props: IProps
): React.ReactElement<IProps> => {
  const { borderStyleKey = "default" } = props;
  const { t } = useTranslation(["component"]);

  const appDispatch = useAppDispatch();

  const post = useAppSelector(postSelector);
  const searchCategory = useAppSelector(searchCategorySelector);

  const onClickHotel: () => void = () => {
    appDispatch(changeSearchCategory(searchCategories.hotel));
  };
  const onClickFlight: () => void = () => {
    appDispatch(changeSearchCategory(searchCategories.flight));
  };
  const onClickDynamicPackage: () => void = () => {
    appDispatch(changeSearchCategory(searchCategories.dynamicPackage));
  };

  const { hotel, flight, dynamicPackage } = searchCategories;

  const renderHotelTab = () => {
    return (
      <div
        css={[tabContainerStyle, tabHotelStyle]}
        key={searchCategories.hotel}
      >
        <div
          onClick={onClickHotel}
          css={[tabStyle, searchCategory === hotel ? tabSelectedStyle : null]}
        >
          <div css={iconStyle}>
            <Icon styleKey="hotel" />
          </div>
          <span css={labelStyle}>{t("search.hotel")}</span>
        </div>
      </div>
    );
  };

  const renderFlightTab = () => {
    return (
      <div
        css={[tabContainerStyle, tabFlightStyle]}
        key={searchCategories.flight}
      >
        <div
          onClick={onClickFlight}
          css={[tabStyle, searchCategory === flight ? tabSelectedStyle : null]}
        >
          <div css={iconStyle}>
            <Icon styleKey="airplane" />
          </div>
          <span css={labelStyle}>{t("search.flight")}</span>
        </div>
      </div>
    );
  };

  const renderDynamicPackageTab = () => {
    return (
      <div
        css={[tabContainerStyle, tabDynamicPackageStyle]}
        key={searchCategories.dynamicPackage}
      >
        <div
          onClick={onClickDynamicPackage}
          css={[
            tabStyle,
            searchCategory === dynamicPackage ? tabSelectedStyle : null,
          ]}
        >
          <div>
            <div css={iconStyle}>
              <Icon styleKey="airplane" />
            </div>
            <span css={verticalMiddleStyle}>+</span>
            <div css={[iconStyle, iconMultiStyle]}>
              <Icon styleKey="hotel" />
            </div>
          </div>
          <span css={labelStyle}>
            {t("search.flight")} + {t("search.hotel")}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div css={blockStyle}>
      <div css={[innerStyle, borderStyle[borderStyleKey]]}>
        {post.searchTypes?.map((searchType) => {
          const camelizedSearchType = camelize(searchType);
          if (camelizedSearchType === searchCategories.hotel) {
            return renderHotelTab();
          } else if (camelizedSearchType === searchCategories.flight) {
            return renderFlightTab();
          } else if (camelizedSearchType === searchCategories.dynamicPackage) {
            return renderDynamicPackageTab();
          }
          return;
        })}
      </div>
    </div>
  );
};

export interface IBorderStyle {
  default: any;
  noRound: any;
}

const blockStyle = css``;
const innerStyle = css`
  display: flex;
  overflow: hidden;
`;
const tabContainerStyle = css`
  box-sizing: border-box;
  overflow: hidden;
  width: auto;
`;
const tabHotelStyle = css`
  width: 33.333%;
  ${moreThanBreakpoint("tablet")} {
    width: auto;
  }
`;
const tabFlightStyle = css`
  width: 33.333%;
  ${moreThanBreakpoint("tablet")} {
    width: auto;
  }
`;
const tabDynamicPackageStyle = css`
  width: 33.333%;
  min-width: 120px;
  ${moreThanBreakpoint("tablet")} {
    width: auto;
  }
`;
const tabStyle = css`
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 0.1rem 1px;
  font-size: ${fontSize.smallest};
  text-align: center;
  height: 44px;
  color: ${accentColor.primaryColor};
  box-sizing: border-box;
  background-color: #fff;
  ${moreThanBreakpoint("tablet")} {
    padding: 0 1rem 1px;
    flex-direction: row;
    align-items: center;
    height: 38px;
    line-height: 41px;
    font-size: ${fontSize.caption};
  }
`;
const tabSelectedStyle = css`
  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 2px;
    background-color: ${accentColor.primaryColor};
  }
`;
const iconStyle = css`
  display: inline-block;
  i {
    font-size: 1.125rem;
    vertical-align: middle;
    ${moreThanBreakpoint("tablet")} {
      position: relative;
      top: -1px;
    }
  }
`;
const iconMultiStyle = css`
  i {
    padding-left: 0.25rem;
    ${moreThanBreakpoint("tablet")} {
      padding-left: 0.25rem;
    }
  }
`;
const labelStyle = css`
  vertical-align: middle;
  padding-top: 0.2rem;
  line-height: 1;
  display: block;
  font-weight: ${fontWeight.bold};
  ${moreThanBreakpoint("tablet")} {
    padding-top: 0;
    padding-left: ${space.atom};
    display: inline;
    line-height: 41px;
  }
`;
const verticalMiddleStyle = css`
  vertical-align: middle;
`;

const borderStyle: IBorderStyle = {
  default: css`
    border-radius: ${borderRadius.normal};
    ${moreThanBreakpoint("tablet")} {
      border-radius: 0;
    }
  `,
  noRound: css`
    border-radius: 0;
  `,
};
